/**
 * Sharing.
 *
 * Two artefacts: spoiler-free text (safe to paste in a group chat before
 * everyone has played) and an image card for the places text is not enough.
 * Both are generated locally; nothing is uploaded.
 */

import { GRADES, type Grade } from '../game/grade';

export interface ShareData {
  puzzleNumber: number;
  weekday: string;
  grade: Grade;
  blendsUsed: number;
  budget: number;
  yourHex: string;
  targetHex: string;
  targetName: string;
  /** Every colour the player put on the bench, for the card's footer strip. */
  trail: string[];
  streak: number;
}

// The public URL. Shown at the foot of the share card and on the last line of
// the copy-to-clipboard text.
export const SHARE_URL = 'https://colordrift.app';
export function shareText(d: ShareData): string {
  const spent = '⬛'.repeat(Math.min(d.blendsUsed, 10));
  const saved = '⬜'.repeat(Math.max(0, Math.min(d.budget - d.blendsUsed, 10)));
  return [
    `COLORDRIFT #${d.puzzleNumber} ${GRADES[d.grade].symbol} ${GRADES[d.grade].label.toUpperCase()}`,
    `${spent}${saved} ${d.blendsUsed}/${d.budget}`,
    d.streak > 1 ? `🔥 ${d.streak} day streak` : '',
    SHARE_URL,
  ]
    .filter(Boolean)
    .join('\n');
}

/** Long edge, in CSS pixels before the DPR scale. */
const W = 1080;
const H = 1350;
const PAD = 76;

export async function shareCard(d: ShareData): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d');
  if (!g) return null;

  const grade = GRADES[d.grade];

  // --- Backdrop: the day's colour, mostly drowned in black -----------------
  g.fillStyle = '#08080b';
  g.fillRect(0, 0, W, H);

  const glow = g.createRadialGradient(W / 2, -180, 0, W / 2, -180, H * 0.95);
  glow.addColorStop(0, withAlpha(d.targetHex, 0.5));
  glow.addColorStop(1, 'rgba(8,8,11,0)');
  g.fillStyle = glow;
  g.fillRect(0, 0, W, H);

  // --- Masthead ------------------------------------------------------------
  g.fillStyle = '#f4f4f6';
  g.font = '800 46px ui-sans-serif, system-ui, sans-serif';
  g.letterSpacing = '10px';
  g.fillText('DRIFT', PAD, PAD + 40);

  g.font = '700 25px ui-sans-serif, system-ui, sans-serif';
  g.letterSpacing = '4px';
  g.fillStyle = 'rgba(244,244,246,0.5)';
  g.fillText(`#${d.puzzleNumber} · ${d.weekday.toUpperCase()}`, PAD, PAD + 88);

  // --- Verdict -------------------------------------------------------------
  g.letterSpacing = '-2px';
  g.font = '800 108px ui-sans-serif, system-ui, sans-serif';
  g.fillStyle = gradeColour(d.grade);
  g.fillText(grade.label, PAD, 320);

  g.font = '400 30px ui-sans-serif, system-ui, sans-serif';
  g.letterSpacing = '0px';
  g.fillStyle = 'rgba(244,244,246,0.6)';
  g.fillText(grade.blurb, PAD, 372);

  // --- The two colours -----------------------------------------------------
  const gap = 24;
  const blockW = (W - PAD * 2 - gap) / 2;
  const blockH = 620;
  const blockY = 440;

  const panel = (x: number, hex: string, label: string, sub: string) => {
    roundRect(g, x, blockY, blockW, blockH, 34);
    g.fillStyle = hex;
    g.fill();
    g.strokeStyle = 'rgba(255,255,255,0.1)';
    g.lineWidth = 2;
    g.stroke();

    const ink = readableOn(hex);
    g.fillStyle = ink;
    g.globalAlpha = 0.68;
    g.font = '800 22px ui-sans-serif, system-ui, sans-serif';
    g.letterSpacing = '3px';
    g.fillText(label, x + 32, blockY + 54);

    g.globalAlpha = 1;
    g.font = '650 30px ui-sans-serif, system-ui, sans-serif';
    g.letterSpacing = '0px';
    g.fillText(sub, x + 32, blockY + blockH - 38);
  };

  panel(PAD, d.yourHex, 'YOURS', hexOf(d.yourHex));
  panel(PAD + blockW + gap, d.targetHex, 'TARGET', d.targetName);

  // --- Trail ---------------------------------------------------------------
  const trailY = blockY + blockH + 56;
  const cell = Math.min(58, (W - PAD * 2 - (d.trail.length - 1) * 10) / Math.max(1, d.trail.length));
  d.trail.slice(0, 14).forEach((hex, i) => {
    roundRect(g, PAD + i * (cell + 10), trailY, cell, cell, 14);
    g.fillStyle = hex;
    g.fill();
  });

  g.fillStyle = 'rgba(244,244,246,0.45)';
  g.font = '700 24px ui-sans-serif, system-ui, sans-serif';
  g.letterSpacing = '3px';
  g.fillText(
    `${d.blendsUsed}/${d.budget} BLENDS${d.streak > 1 ? ` · ${d.streak} DAY STREAK` : ''}`,
    PAD,
    trailY + cell + 58,
  );

  g.font = '700 26px ui-sans-serif, system-ui, sans-serif';
  g.letterSpacing = '2px';
  g.fillStyle = 'rgba(244,244,246,0.32)';
  g.fillText(SHARE_URL.toUpperCase(), PAD, H - PAD);

  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/png'));
}

/**
 * Hand the share off. Returns which route was taken so the UI can say so,
 * because "Shared" and "Copied" are different things to a player.
 */
export async function share(d: ShareData): Promise<'shared' | 'copied' | 'failed'> {
  const text = shareText(d);
  const nav = navigator as Navigator & {
    share?: (data: { files?: File[]; text?: string; title?: string }) => Promise<void>;
    canShare?: (data: { files?: File[] }) => boolean;
  };

  try {
    if (nav.share) {
      const blob = await shareCard(d);
      const file = blob ? new File([blob], `prism-${d.puzzleNumber}.png`, { type: 'image/png' }) : null;

      if (file && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], text, title: `ColorDrift #${d.puzzleNumber}` });
        return 'shared';
      }
      await nav.share({ text, title: `ColorDrift #${d.puzzleNumber}` });
      return 'shared';
    }
  } catch (err) {
    // AbortError means the player dismissed the sheet; anything else falls through.
    if (err instanceof Error && err.name === 'AbortError') return 'shared';
  }

  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}

// ---------------------------------------------------------------------------
// Canvas helpers
// ---------------------------------------------------------------------------

function roundRect(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function gradeColour(grade: Grade): string {
  return {
    perfect: '#7ff2c0',
    excellent: '#a5d8ff',
    great: '#d9c9ff',
    close: '#ffd9a0',
    miss: '#ff9d9d',
  }[grade];
}

function channels(hex: string): [number, number, number] {
  const h = hex.replace('#', '').padEnd(6, '0');
  return [
    parseInt(h.slice(0, 2), 16) || 0,
    parseInt(h.slice(2, 4), 16) || 0,
    parseInt(h.slice(4, 6), 16) || 0,
  ];
}

function withAlpha(hex: string, a: number): string {
  const [r, g, b] = channels(hex);
  return `rgba(${r},${g},${b},${a})`;
}

function hexOf(hex: string): string {
  return hex.toUpperCase();
}

/** Perceptual luminance, so card text never lands on a same-value background. */
function readableOn(hex: string): string {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.42 ? '#0a0a0c' : '#ffffff';
}
